import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createStorefrontCheckout } from '@/lib/shopify';
import type { ShopifyProduct } from '@/lib/shopify';

// Icon addon product variant ID from Shopify
const ICON_ADDON_VARIANT_ID = 'gid://shopify/ProductVariant/49951136153793';
const ICON_PRICE = 60;

export interface EngravingData {
  font: string;
  texts: string[]; // Array of texts for multiple engraving locations
  price: number;
}

export interface CartItem {
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  quantity: number;
  selectedOptions?: Array<{
    name: string;
    value: string;
  }>;
  variantImage?: {
    url: string;
    altText?: string;
  };
  engraving?: EngravingData;
  icons?: string[]; // Icon IDs for engraving (up to 2)
}

// Helper function to create a unique key for cart items
const getItemKey = (item: CartItem) => {
  const engravingKey = item.engraving 
    ? `${item.engraving.font}|${item.engraving.texts.join('|')}` 
    : '';
  const iconsKey = item.icons?.join('|') || '';
  return `${item.variantId}|${engravingKey}|${iconsKey}`;
};

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isCartOpen: boolean;
  
  addItem: (item: CartItem) => void;
  updateQuantity: (itemKey: string, quantity: number) => void;
  removeItem: (itemKey: string) => void;
  clearCart: () => void;
  setCartId: (cartId: string) => void;
  setCheckoutUrl: (url: string) => void;
  setLoading: (loading: boolean) => void;
  setCartOpen: (open: boolean) => void;
  createCheckout: (locale?: string, discountCode?: string) => Promise<void>;
  getTotalPrice: () => number;
  getTotalItems: () => number;
  getItemKey: (item: CartItem) => string;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isCartOpen: false,

      getItemKey,

      addItem: (item) => {
        const { items } = get();
        
        const itemKey = getItemKey(item);
        const existingItem = items.find(i => getItemKey(i) === itemKey);
        
        if (existingItem) {
          set({
            items: items.map(i =>
              getItemKey(i) === itemKey
                ? { ...i, quantity: i.quantity + item.quantity }
                : i
            ),
            isCartOpen: true
          });
        } else {
          set({ items: [...items, item], isCartOpen: true });
        }
      },

      updateQuantity: (itemKey, quantity) => {
        if (quantity <= 0) {
          get().removeItem(itemKey);
          return;
        }
        
        set({
          items: get().items.map(item =>
            getItemKey(item) === itemKey ? { ...item, quantity } : item
          )
        });
      },

      removeItem: (itemKey) => {
        set({
          items: get().items.filter(item => getItemKey(item) !== itemKey)
        });
      },

      clearCart: () => {
        set({ items: [], cartId: null, checkoutUrl: null });
      },

      setCartId: (cartId) => set({ cartId }),
      setCheckoutUrl: (checkoutUrl) => set({ checkoutUrl }),
      setLoading: (isLoading) => set({ isLoading }),
      setCartOpen: (isCartOpen) => set({ isCartOpen }),

      createCheckout: async (locale?: string, discountCode?: string) => {
        const { items, setLoading, setCheckoutUrl, setCartId } = get();
        
        if (items.length === 0) {
          console.warn('Cannot create checkout with empty cart');
          return;
        }

        setLoading(true);
        try {
          const lineItems: Array<{
            variantId: string;
            quantity: number;
            attributes?: Array<{ key: string; value: string }>;
          }> = [];
          
          // Calculate total icons to add as addon product
          let totalIconAddons = 0;

          items.forEach(item => {
            const attributes: Array<{ key: string; value: string }> = [];
            
            // Add engraving properties if present
            if (item.engraving) {
              // Add font type
              attributes.push({
                key: 'סוג פונט',
                value: item.engraving.font,
              });
              
              // Add each engraving text with numbered keys
              item.engraving.texts.forEach((text, index) => {
                if (text.trim()) {
                  const key = item.engraving!.texts.length > 1 
                    ? `טקסט חריטה ${index + 1}` 
                    : 'טקסט חריטה';
                  attributes.push({
                    key,
                    value: text,
                  });
                }
              });
            }

            // Add icons if present and count for addon
            if (item.icons && item.icons.length > 0) {
              if (item.icons.length === 1) {
                attributes.push({
                  key: 'סמל לחריטה',
                  value: item.icons[0],
                });
              } else {
                item.icons.forEach((iconId, index) => {
                  attributes.push({
                    key: `סמל לחריטה ${index + 1}`,
                    value: iconId,
                  });
                });
              }
              // Count total icon addons needed (per item, not per quantity)
              totalIconAddons += item.icons.length;
            }
            
            lineItems.push({
              variantId: item.variantId,
              quantity: item.quantity,
              attributes: attributes.length > 0 ? attributes : undefined,
            });
          });

          // Add icon addon product if there are any icons
          if (totalIconAddons > 0) {
            lineItems.push({
              variantId: ICON_ADDON_VARIANT_ID,
              quantity: totalIconAddons,
            });
          }

          let checkout;
          try {
            checkout = await createStorefrontCheckout(lineItems, locale, discountCode);
          } catch (firstError) {
            // If checkout failed and we had icon addons, retry without them
            if (totalIconAddons > 0) {
              console.warn('Checkout failed with icon addon, retrying without it:', firstError);
              const lineItemsWithoutAddon = lineItems.filter(
                li => li.variantId !== ICON_ADDON_VARIANT_ID
              );
              checkout = await createStorefrontCheckout(lineItemsWithoutAddon, locale, discountCode);
            } else {
              throw firstError;
            }
          }
          
          setCartId(checkout.id);
          setCheckoutUrl(checkout.webUrl);
          
          // Redirect to checkout in same tab
          window.location.href = checkout.webUrl;
        } catch (error) {
          console.error('Failed to create checkout:', error);
          throw error;
        } finally {
          setLoading(false);
        }
      },

      getTotalPrice: () => {
        const { items } = get();
        const ICON_PRICE = 60; // Price per icon in ILS
        return items.reduce((total, item) => {
          const basePrice = parseFloat(item.price.amount);
          const iconsPrice = (item.icons?.length || 0) * ICON_PRICE;
          const itemTotal = (basePrice + iconsPrice) * item.quantity;
          return total + itemTotal;
        }, 0);
      },

      getTotalItems: () => {
        const { items } = get();
        return items.reduce((total, item) => total + item.quantity, 0);
      },
    }),
    {
      name: 'alice-cart-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        items: state.items,
        cartId: state.cartId,
        checkoutUrl: state.checkoutUrl,
      }),
    }
  )
);
